import { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const GRID_CELLS = [
  { row: 0, col: 0, answer: 'I', clueNum: '1', direction: 'down' },
  { row: 1, col: 3, answer: 'C', clueNum: '2', direction: 'down' },
  { row: 2, col: 0, answer: 'E', clueNum: '3', direction: 'across' },
  { row: 2, col: 1, answer: 'S', direction: 'across' },
  { row: 2, col: 2, answer: 'C', direction: 'across' },
  { row: 2, col: 3, answer: 'A', direction: 'both' },
  { row: 2, col: 4, answer: 'P', direction: 'across' },
  { row: 2, col: 5, answer: 'E', direction: 'across' },
  { row: 3, col: 3, answer: 'N', direction: 'down' },
];

const TOTAL_ROWS = 4;
const TOTAL_COLS = 6;

function getCellKey(row, col) { return `${row}-${col}`; }

export default function CrosswordPuzzle() {
  const [values, setValues] = useState({});
  const [solved, setSolved] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const inputRefs = useRef({});
  const { t } = useLanguage();

  const cellMap = {};
  GRID_CELLS.forEach(cell => { cellMap[getCellKey(cell.row, cell.col)] = cell; });

  const handleChange = useCallback((row, col, val) => {
    const letter = val.toUpperCase().replace(/[^A-Z]/g, '').slice(-1);
    const newValues = { ...values, [getCellKey(row, col)]: letter };
    setValues(newValues);
    const allCorrect = GRID_CELLS.every(cell => newValues[getCellKey(cell.row, cell.col)] === cell.answer);
    if (allCorrect) { setSolved(true); setShowMessage(true); }
    if (letter) {
      const cellIndex = GRID_CELLS.findIndex(c => c.row === row && c.col === col);
      if (cellIndex < GRID_CELLS.length - 1) {
        const next = GRID_CELLS[cellIndex + 1];
        inputRefs.current[getCellKey(next.row, next.col)]?.focus();
      }
    }
  }, [values]);

  const handleKeyDown = (e, row, col) => {
    if (e.key === 'Backspace' && !values[getCellKey(row, col)]) {
      const cellIndex = GRID_CELLS.findIndex(c => c.row === row && c.col === col);
      if (cellIndex > 0) { const prev = GRID_CELLS[cellIndex - 1]; inputRefs.current[getCellKey(prev.row, prev.col)]?.focus(); }
    }
  };

  const sendToFacebook = () => {
    window.open('https://m.me/okiescape?text=I%20CAN%20ESCAPE%21%20I%20solved%20the%20crossword%20puzzle%20on%20your%20website!', '_blank');
  };

  const CLUES = {
    down: [{ num: '1', clue: t('clueI') }, { num: '2', clue: t('clueCan') }],
    across: [{ num: '3', clue: t('clueEscape') }],
  };

  return (
    <section data-testid="crossword-section" id="crossword-section" className="py-4 sm:py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight uppercase text-foreground">
            {t('solveThe')} <span className="text-brand">{t('puzzle')}</span>
          </h2>
          <p className="font-sans text-base sm:text-lg text-muted-foreground mt-4 max-w-xl mx-auto">{t('crosswordDesc')}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-card border border-border rounded-3xl p-5 sm:p-8">
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            <div className="flex-1 flex justify-center">
              <div className="inline-grid gap-1" style={{ gridTemplateColumns: `repeat(${TOTAL_COLS}, 40px)`, gridTemplateRows: `repeat(${TOTAL_ROWS}, 40px)` }}>
                {Array.from({ length: TOTAL_ROWS }).map((_, row) =>
                  Array.from({ length: TOTAL_COLS }).map((_, col) => {
                    const key = getCellKey(row, col);
                    const cell = cellMap[key];
                    if (!cell) return <div key={key} className="w-10 h-10" />;
                    const isCorrect = values[key] === cell.answer;
                    const hasValue = !!values[key];
                    return (
                      <div key={key} className="relative w-10 h-10">
                        {cell.clueNum && <span className="absolute top-0 left-0.5 text-[8px] font-sans font-bold text-muted-foreground z-10">{cell.clueNum}</span>}
                        <input ref={el => (inputRefs.current[key] = el)} data-testid={`crossword-cell-${row}-${col}`} type="text" maxLength={1} value={values[key] || ''} onChange={e => handleChange(row, col, e.target.value)} onKeyDown={e => handleKeyDown(e, row, col)} disabled={solved}
                          className={`w-10 h-10 text-center font-heading text-lg uppercase border-2 rounded-lg outline-none transition-all duration-200 ${solved && isCorrect ? 'bg-brand/20 border-brand text-brand shadow-[0_0_10px_rgba(227,37,16,0.3)]' : hasValue && !isCorrect ? 'bg-card border-border text-foreground' : 'bg-card border-border text-foreground hover:border-brand/50 focus:border-brand focus:shadow-[0_0_10px_rgba(227,37,16,0.2)]'}`} />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            <div className="flex-1 space-y-6">
              <div>
                <h4 className="font-heading text-lg tracking-wide uppercase text-brand mb-3">{t('down')}</h4>
                {CLUES.down.map(c => <p key={c.num} className="font-sans text-sm text-muted-foreground mb-1"><span className="font-bold text-foreground">{c.num}.</span> {c.clue}</p>)}
              </div>
              <div>
                <h4 className="font-heading text-lg tracking-wide uppercase text-brand mb-3">{t('across')}</h4>
                {CLUES.across.map(c => <p key={c.num} className="font-sans text-sm text-muted-foreground"><span className="font-bold text-foreground">{c.num}.</span> {c.clue}</p>)}
              </div>
            </div>
          </div>
          {showMessage && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="mt-8 text-center p-6 bg-brand/10 border border-brand/30 rounded-2xl">
              <h3 className="font-heading text-3xl sm:text-4xl tracking-tight uppercase text-brand neon-text-glow mb-2">{t('iCanEscape')}</h3>
              <p className="font-sans text-muted-foreground mb-6">{t('crosswordSolved')}</p>
              <button onClick={sendToFacebook} data-testid="crossword-facebook-btn" className="inline-block font-heading tracking-widest uppercase bg-brand text-white px-8 py-3 rounded-full hover:bg-brand-hover transition-colors neon-glow text-lg">{t('messageOnFacebook')}</button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
